import { State, Action, StateContext, Selector, Store } from '@ngxs/store';
import { Alert } from 'app/models/dto/alert.model';
import { Deal, NewDeal } from 'app/models/dto/deal.model';
import { EventBusService } from 'app/modules/shared/services/event-bus.service';
import { DealsService } from 'app/services/deals/deals.service';
import { LenderApiService } from 'app/services/lender-api/lender-api.service';
import { PusherService } from 'app/modules/shared/services/pusher.service';
import { GetDealOffers, UpdateOffer } from 'app/store/offers/offer.actions';
import { forkJoin, Subject, throwError, of } from 'rxjs';
import { catchError, map, switchMap, take, takeUntil, tap } from 'rxjs/operators';
import { CreateNewDealAlert } from '../deal-alerts/deal-alerts.actions';
import { GetDeals, UpdateDeal, FundDeal, CreateDeal, ClearDealStore, UpdateDealDetails, SaveDealStatus, RefreshDealStatus, SubscribeToDealStatus, SetLastOffer, SetHighestOffer, CreateDeals, GetLenderDealStatus } from './deal.actions';
import { GetBorrowerApplications } from '../application/application.actions';
import { OfferState } from 'app/store/offers/offer.state';
import { LoanProduct } from 'app/models/dto/loan-product.model';
import { RejectedDealReasonService } from 'app/services/rejectedDeal-reason.service';
import { Injectable } from '@angular/core';
import { isEmpty } from 'lodash';
import { CreateNewBorrowerAlert } from 'app/store/borrower/alerts/borrower-alerts.actions';
import { ApprovalsState } from 'app/store/offers/approvals.state';

export class DealStateModel {
  borrowerId: null | number;
  deals: Deal[];
  loading: boolean;
}

@State<DealStateModel>({
  name: 'Deals',
  defaults: {
    borrowerId: null,
    deals: [],
    loading: true
  }
})

@Injectable()

export class DealState {

  @Selector()
  static deals(state: DealStateModel) {
    return state.deals;
  }

  @Selector()
  static activeDeals(state: DealStateModel) {
    return state.deals.filter(d => d.stage !== 'funded' && d.stage !== 'inactive');
  }

  @Selector()
  static fundedDeals(state: DealStateModel) {
    return state.deals.filter(d => d.stage === 'funded');
  }

  @Selector()
  static inactiveDeals(state: DealStateModel) {
    return state.deals.filter(d => d.stage === 'funded' || d.stage === 'inactive');
  }

  @Selector()
  static loading(state: DealStateModel) {
    return state.loading;
  }

  @Selector()
  static dealDateClosed(state: DealStateModel) {
    return (dealId: number) => state.deals.find(deal => deal.id === dealId)?.dateClosed;
  }

  destroyed$ = new Subject<boolean>();

  constructor(
    private dealsService: DealsService,
    private lenderApiService: LenderApiService,
    private store: Store,
    private eventBusService: EventBusService,
    private pusherService: PusherService,
    private rejectedDealReasonService: RejectedDealReasonService
  ) { }

  @Action(GetDeals, { cancelUncompleted: true })
  getDeals({ getState, patchState }: StateContext<DealStateModel>, { borrowerId, refresh = false }: GetDeals) {
    const prevBorrowerId = getState().borrowerId;

    if (borrowerId === prevBorrowerId && refresh === false) {
      return;
    }

    if (borrowerId !== prevBorrowerId) {
      // reset state
      patchState({
        borrowerId,
        deals: [],
        loading: true
      });
    } else {
      patchState({
        loading: true
      });
    }

    // load deal data
    this.store.dispatch(new GetDealOffers(borrowerId));
    this.store.dispatch(new GetBorrowerApplications(borrowerId));
    return this.dealsService.get(borrowerId).pipe(
      catchError(() => {
        this.store.dispatch(new CreateNewBorrowerAlert({
          level: 'error',
          message: 'Failed to load borrower deals. Please refresh and try again.',
          dismissDelay: 5000,
        }));
        return of();
      }),
      map(deals => {
        deals.forEach(deal => {
          deal.supportedApiActions = this.lenderApiService.getSupportedApiActions(deal);
          this.store.select(OfferState.lastDealOffer).pipe(
            map(filterFn => filterFn(deal.id)),
            takeUntil(this.destroyed$)).subscribe(lastOffer => {
              deal.lastOffer = lastOffer;
              this.store.select(OfferState.highestDealOffer).pipe(
                map(filterFn => filterFn(deal.id)),
                takeUntil(this.destroyed$)).subscribe(highestOffer => {
                  deal.highestOffer = highestOffer;
                });
            });
          this.store.select(ApprovalsState.highestDealApproval).pipe(
            map(filterFn => filterFn(deal.id)),
            takeUntil(this.destroyed$)).subscribe(highestApproval => {
              deal.highestApproval = highestApproval;
            });
        });
        patchState({
          deals,
          loading: false
        });
      }));
  }

  @Action(CreateDeals)
  createDeals({ getState }: StateContext<DealStateModel>, { borrowerId, userId, loanProducts, rejectedReason }: CreateDeals) {
    const newDeals = loanProducts.map(loanProduct => this.loanProductToNewDeal(borrowerId, userId, loanProduct));
    const createDeal = newDeal => this.store.dispatch(new CreateDeal(newDeal));
    const head = newDeals[0];
    const tail = newDeals.slice(1);
    return createDeal(head).pipe(
      switchMap(() => forkJoin([
          ...tail.map(newDeal => createDeal(newDeal))
        ])),
      tap(() => {
        this.saveRejectedReasons(loanProducts, getState().deals, rejectedReason);
      })
    );
  }

  @Action(CreateDeal)
  createDeal({ getState, patchState }: StateContext<DealStateModel>, { newDeal }: CreateDeal) {
    return this.dealsService.create(newDeal).pipe(catchError(err => throwError(() => err)), tap(deal => {
      deal.supportedApiActions = this.lenderApiService.getSupportedApiActions(deal);
      this.store.select(OfferState.lastDealOffer).pipe(
        map(filterFn => filterFn(deal.id)),
        takeUntil(this.destroyed$)).subscribe(lastOffer => {
          deal.lastOffer = lastOffer;
          this.store.select(OfferState.highestDealOffer).pipe(
            map(filterFn => filterFn(deal.id)),
            takeUntil(this.destroyed$)).subscribe(highestOffer => {
              deal.highestOffer = highestOffer;
            });
        });
      patchState({ deals: [deal, ...getState().deals] });
      this.eventBusService.publish(new this.eventBusService.types.GoToActiveTabEvent);
    }));
  }

  @Action(UpdateDeal)
  updateDeal({ getState, patchState }: StateContext<DealStateModel>, { dealId, dealUpdates }: UpdateDeal) {
    return this.dealsService.update(dealId, dealUpdates).pipe(catchError(err => {
      const newAlert: Alert = {
        level: 'error',
        message: 'There was an error while attempting to update this deal. Please try again.',
      };
      this.store.dispatch(new CreateNewDealAlert(dealId, newAlert));
      return of();
    }), tap(updatedDeal => {
      const dealsStateCopy = [...getState().deals];
      const dealIndex = dealsStateCopy.findIndex(d => d.id === dealId);
      dealsStateCopy[dealIndex] = { ...dealsStateCopy[dealIndex], ...updatedDeal }; // merge new deal data w/ previous to not lose values
      patchState({ deals: dealsStateCopy });
    }));
  }

  @Action(SetLastOffer)
  setLastOffer({ getState, patchState }: StateContext<DealStateModel>, { dealId }: SetLastOffer) {
      const deal = getState().deals.find(d => d.id === dealId);
      if (deal && !deal.lastOffer) {
        const dealCopy = { ...deal };
        this.store.select(OfferState.lastDealOffer).pipe(
          map(filterFn => filterFn(dealId)),
          takeUntil(this.destroyed$)).subscribe(lastOffer => {
            dealCopy.lastOffer = lastOffer;
          });
        const dealsStateCopy = [...getState().deals];
        const dealIndex = dealsStateCopy.findIndex(d => d.id === dealId);
        dealsStateCopy[dealIndex] = dealCopy;
        patchState({ deals: dealsStateCopy });
      }
  }

  @Action(SetHighestOffer)
  setHighestOffer({ getState, patchState }: StateContext<DealStateModel>, { dealId }: SetHighestOffer) {
      const deal = getState().deals.find(d => d.id === dealId);
      if (deal) {
        const dealCopy = { ...deal };
        this.store.select(OfferState.highestDealOffer).pipe(
          map(filterFn => filterFn(dealId)),
          takeUntil(this.destroyed$)).subscribe(highestOffer => {
            dealCopy.highestOffer = highestOffer;
          });
        const dealsStateCopy = [...getState().deals];
        const dealIndex = dealsStateCopy.findIndex(d => d.id === dealId);
        dealsStateCopy[dealIndex] = dealCopy;
        patchState({ deals: dealsStateCopy });
      }
  }

  @Action(UpdateDealDetails)
  UpdateDealDetails({ getState, patchState }: StateContext<DealStateModel>, { dealId, dealUpdates }: UpdateDealDetails) {
    return this.dealsService.update(dealId, dealUpdates).pipe(catchError(err => {
      const newAlert: Alert = {
        level: 'error',
        message: 'There was an error while attempting to update this deal. Please try again.',
      };
      this.store.dispatch(new CreateNewDealAlert(dealId, newAlert));
      return of();
    }), tap(updatedDeal => {
      const dealsStateCopy = [...getState().deals];
      const dealIndex = dealsStateCopy.findIndex(d => d.id === dealId);
      const dealCopy = dealsStateCopy[dealIndex];
      Object.keys(dealUpdates).forEach(val => {
        dealCopy[val] = dealUpdates[val];
      });
      dealCopy.lenderUserId = updatedDeal.lenderUserId;
      dealCopy.lender_user = updatedDeal.lender_user;
      dealsStateCopy[dealIndex] = dealCopy;
      patchState({ deals: dealsStateCopy });
    }));
  }

  @Action(FundDeal)
  fundDeal({ }: StateContext<DealStateModel>, { offer, deal }: FundDeal) {
    return this.store.dispatch(new UpdateOffer(offer.id, offer)).pipe(catchError(err => throwError(() => err)), tap(() => {
      deal.status = 'current';
      deal.acceptedOfferId = offer.id;
      this.store.dispatch(new UpdateDeal(deal.id, deal)).pipe(take(1), catchError(err => throwError(() => err))).subscribe(() => {
        this.eventBusService.publish(new this.eventBusService.types.GoToFundedTabEvent);
      });
    }));
  }

  @Action(ClearDealStore)
  clearDealStore({ patchState }: StateContext<DealStateModel>, { }: ClearDealStore) {
    this.destroyed$.next(true);
    patchState({
      borrowerId: null,
      deals: [],
      loading: true,
    });
  }

  /**
   * save deal status state
   *
   * @param param0
   * @param param1
   */
	@Action(SaveDealStatus)
  saveDealStatus(
    { getState, patchState }: StateContext<DealStateModel>,
    { dealId, stage, status, reason, otherReason, subReasons }: SaveDealStatus) {
    return this.dealsService.update(dealId, { stage, status, reason, otherReason, subReasons }).pipe(catchError(err => {
      const newAlert: Alert = {
        level: 'error',
        message: 'There was an error while attempting to update this deal\'s status. Please try again.',
      };
      this.store.dispatch(new CreateNewDealAlert(dealId, newAlert));
      return of();
    }), tap(updatedDeal => {
      const modifiedState = getState().deals.map(deal => {
        if (deal.id === dealId) {
          return { ...deal, ...updatedDeal };
        }
        return deal;
      });
      patchState({ deals: modifiedState });
    }));
  }

  @Action(RefreshDealStatus)
  refreshDealStatus({ getState, patchState }: StateContext<DealStateModel>, { dealId }: RefreshDealStatus) {
    return this.dealsService.getDealById(dealId).pipe(catchError(err => {
      const newAlert: Alert = {
        level: 'error',
        message: 'There was an error while attempting to update this deal. Please try again.',
      };
      this.store.dispatch(new CreateNewDealAlert(dealId, newAlert));
      return of();
    }), tap(refreshedDeal => {
      const { status, stage, statusOptions, last_inactive_status, dead_disposition_deals } = refreshedDeal;
      const updatedState = getState().deals.map(deal => {
        if (deal.id === dealId) {
          deal.statusOptions = statusOptions;
          deal.stage = stage;
          deal.status = status;
          deal.dead_disposition_deals = dead_disposition_deals;
          deal.last_inactive_status = last_inactive_status;
        }
        return deal;
      });
      patchState({ deals: updatedState });
    }));
  }

  @Action(GetLenderDealStatus)
  getLenderDealStatus({ getState, patchState }: StateContext<DealStateModel>, { deal }: GetLenderDealStatus) {
    //Only dispatch if On Deck & there is a lenderCustomerId
    if (deal && deal.loanProductLenderId === 224 && !isEmpty(deal.lenderCustomerId)) {
      return this.lenderApiService.getDealStatus(deal.id).pipe(take(1)).subscribe({
        next: (res) => {
            if (res && res.contactStatus && res.contactStatus !== deal.lenderApiStatus) {
              deal.lenderApiStatus = res.contactStatus;
              deal.status = res.outcomeStatus;
              return deal;
            }
        },
        error: (res) => {
          console.warn('Unable to retrieve On Deck status: ' + res);
        }
      });
    }

    return deal;
    //deal.lenderStatus = this.lenderApiService.getDealStatus(dealId);
  }

  @Action(SubscribeToDealStatus)
  subscribeToDealStatus({}: StateContext<DealStateModel>, { dealId }: SubscribeToDealStatus) {
    this.pusherSubscribe(dealId);
  }

  pusherSubscribe(dealId: number) {
    this.pusherService
      .subscribe(`deal-${dealId}-status`)
      .bind_global(this.handlePusherEvent.bind(this, dealId));
  }

  handlePusherEvent(dealId: number, eventName: string) {
    if (eventName.startsWith('deal-status-')) {
      this.store.dispatch(new RefreshDealStatus(dealId));
    }
  }

  private loanProductToNewDeal(borrowerId: number, userId: number, loanProduct: LoanProduct): NewDeal {
    return {
      borrowerId,
      lenderUserId: userId,
      loanProductId: loanProduct.id,
      loanProductLenderId: loanProduct.lenderId,
      stage: 'application',
      status: 'appInProgress',
      type: null,
    };
  }

  private saveRejectedReasons(loanProducts: LoanProduct[], deals: Deal[], rejectedReason: { reason: string, comment: string } | null) {
    if (!rejectedReason) {
      return;
    }

    // build rejected reason for each deal on rejected loan product
    const rejectedReasons = loanProducts
      .filter(loanProduct => loanProduct.matchStatus === 'rejected')
      .map(loanProduct => deals.find(deal => deal.loanProductId === loanProduct.id))
      .filter(x => !!x)
      .map(deal => ({ ...rejectedReason, dealId: (deal as Deal).id }));

    this.rejectedDealReasonService.post(rejectedReasons).subscribe(() => { });
  }
}
